import { withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc, WeightRules, WeightState } from '../types.ts';
import { attackHero, stepToward } from './enemies.ts';
import { ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { weightPull } from './weight-layout.ts';

/**
 * The counterweight (docs/sendas/guerrero.md, chapter 2, act 3, the test: «El contrapeso»; the rule and its generator
 * in battle/weight-layout.ts). Three spans in a row, each hung from its pan; a span comes down level when what pulls
 * it equals its post. The middle pan pulls the outer ones (half its weight each, rounded down); the outer ones pull the
 * middle up (a quarter of their weight together, rounded down). The five shields all hang (a shield on the ground
 * won't let the bolts go in), the two stones if they're needed. A tap on a piece and a pan sends the hero: it walks to
 * the piece, picks it up, carries it (slower) and sets it on the pan; anything can be taken off and moved again, and
 * nothing is lost by trying but time.
 *
 * The shell serpent travels under the stone out of reach, and every so often comes up by the heaviest pan (the rock
 * cracking `warn` before). Up, `scare` blows soon after send it down empty-handed; else it throws that pan's heaviest
 * piece over the edge: a shield hangs from its chain under the lip and Kel hauls it back up (if he's let work); a stone
 * goes down the drop and the pile has another a little later. Levelled with every shield hung, the spans come down
 * together, the chains taking the weight, and Kel drives the bolts: won (a serpent still alive sinks for good).
 *
 * Cost: seven pieces and one serpent, looked at once a tick.
 */

const fresh = (r: WeightRules): WeightState => ({
  on: r.pieces.map(() => -1),
  back: r.pieces.map(() => 0),
  job: -1,
  to: -1,
  held: 0,
  haul: -1,
  hauled: 0,
  level: -1,
  snake: 0,
  phase: 0,
  pan: -1,
  since: 0,
  blows: 0,
  took: false,
});

const kelOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'kel');

/** What each pan weighs as it stands (left, middle, right). */
export const panWeights = (r: WeightRules, w: WeightState): [number, number, number] => {
  const out: [number, number, number] = [0, 0, 0];
  w.on.forEach((p, i) => {
    if (p >= 0 && p <= 2) out[p] = (out[p] as number) + (r.pieces[i] as { w: number }).w;
  });
  return out;
};

/** What each span feels, and whether it hangs level: the rule's count. */
export const panPulls = (r: WeightRules, w: WeightState): [number, number, number] => {
  const [l, m, rr] = panWeights(r, w);
  return weightPull(l, m, rr);
};

/** Every shield hung on a pan. */
const shieldsUp = (r: WeightRules, w: WeightState): boolean => r.pieces.every((p, i) => p.stone || (w.on[i] as number) >= 0);

/** Each span level (its pull at its post) and every shield hung: the bolts can go in. */
export const weightLevel = (r: WeightRules, w: WeightState): boolean => {
  const p = panPulls(r, w);
  return shieldsUp(r, w) && p.every((x, i) => x === r.posts[i]);
};

/** Where a piece is to be picked up (null: out of reach, hanging or down the drop). */
const whereIs = (r: WeightRules, w: WeightState, p: number): { x: number; y: number } | null => {
  const on = w.on[p] as number;
  if (on === -1) return r.pile;
  if (on >= 0 && on <= 2) return r.pans[on] as { x: number; y: number };
  return null;
};

const spotOf = (r: WeightRules, to: number): { x: number; y: number } => (to >= 0 && to <= 2 ? (r.pans[to] as { x: number; y: number }) : r.pile);

/**
 * The hero sent with a piece to a pan (0 to 2) or back to the pile (3): it goes for the piece (unless in its hands
 * already), then to where it goes. A piece already in its hands goes back to the pile first if another is asked for.
 */
export const weightOrder = (s: BattleState, h: Hero, p: number, to: number): void => {
  const r = s.rules?.senda?.weight;
  const w = s.senda?.weight;
  if (!r || !w || p < 0 || p >= r.pieces.length || to < 0 || to > 3) return;
  const on = w.on[p] as number;
  if (on !== -2 && !whereIs(r, w, p)) return;
  // Already there: nothing to do.
  if ((to === 3 && on === -1) || on === to) return;
  for (let i = 0; i < w.on.length; i++) if (i !== p && w.on[i] === -2) w.on[i] = -1;
  w.job = p;
  w.to = to;
  w.held = 0;
  const at = on === -2 ? spotOf(r, to) : (whereIs(r, w, p) as { x: number; y: number });
  orderMove(s, h, at.x, at.y + 600);
  h.target = 0;
  h.forced = false;
};

/** The hero's hands: standing by the piece it picks it up, by the pan it sets it on. */
const heroWork = (s: BattleState, r: WeightRules, w: WeightState): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const p = w.job;
  const on = w.on[p] as number;
  // Gone over the edge while the hero went for it: the job is off.
  if (on === -3 || on === -4) {
    w.job = -1;
    return;
  }
  const carrying = on === -2;
  const at = carrying ? spotOf(r, w.to) : whereIs(r, w, p);
  if (!at) return;
  if (h.moving || !withinRange(h.x, h.y, at.x, at.y, r.reach)) {
    w.held = 0;
    // In the hands and standing still somewhere else (the order was cut short): on to where it goes.
    if (!h.moving && carrying) orderMove(s, h, at.x, at.y + 600);
    return;
  }
  if (++w.held < (carrying ? r.put : r.pick)) return;
  w.held = 0;
  if (!carrying) {
    w.on[p] = -2;
    ev(s, { t: 'senda', kind: 'pick', npc: '', x: at.x, y: at.y, n: p });
    const to = spotOf(r, w.to);
    orderMove(s, h, to.x, to.y + 600);
    return;
  }
  w.on[p] = w.to === 3 ? -1 : w.to;
  ev(s, { t: 'senda', kind: 'set', npc: '', x: at.x, y: at.y, n: w.to });
  w.job = -1;
};

/** The heaviest pan (the first of the heaviest), −1 if all are empty. */
const heaviest = (r: WeightRules, w: WeightState): number => {
  const ws = panWeights(r, w);
  let best = -1;
  for (let i = 0; i < 3; i++) if ((ws[i] as number) > 0 && (best < 0 || (ws[i] as number) > (ws[best] as number))) best = i;
  return best;
};

/** Where the serpent comes up by a pan: beside it, on the land. */
const besidePan = (r: WeightRules, pan: number): { x: number; y: number } => {
  const p = r.pans[pan] as { x: number; y: number };
  return { x: p.x + 750, y: p.y + 450 };
};

/** The serpent: under the stone (out of reach), the rock cracking, up by a pan. */
const serpentTick = (s: BattleState, r: WeightRules, w: WeightState, cue: (c: string) => void): void => {
  const S = r.serpent;
  let e = w.snake ? findEnemy(s, w.snake) : null;
  if (!w.snake) {
    e = spawnEnemy(s, 'shellSerpent', 1, false, 0, r.pile.x, r.pile.y + 1800, S.hp);
    e.phase = 1;
    w.snake = e.id;
    w.since = s.tick;
  }
  if (!e || e.hp <= 0) return;
  const t = s.tick - w.since;
  if (w.phase === 0) {
    // Under the stone: it drifts toward the heaviest pan and waits its time.
    e.phase = 1;
    e.anim = 'walk';
    const wait = (w.took || w.blows ? S.every : s.waveT < S.first ? S.first : S.every) - S.warn;
    const pan = heaviest(r, w);
    if (pan >= 0) {
      const b = besidePan(r, pan);
      stepToward(e, b.x, b.y, 60);
    }
    if (t >= wait && pan >= 0) {
      w.phase = 1;
      w.pan = pan;
      w.since = s.tick;
      const b = besidePan(r, pan);
      e.x = b.x;
      e.y = b.y;
      ev(s, { t: 'senda', kind: 'crack', npc: '', x: b.x, y: b.y, n: pan });
    }
    return;
  }
  if (w.phase === 1) {
    e.phase = 1;
    if (t >= S.warn) {
      w.phase = 2;
      w.since = s.tick;
      w.blows = 0;
      w.took = false;
      e.phase = 2;
      e.anim = 'idle';
      ev(s, { t: 'senda', kind: 'rise', npc: '', x: e.x, y: e.y, n: w.pan });
      cue('rise');
    }
    return;
  }
  // Up: blows soon after scare it off; else it throws the pan's heaviest piece; it bites what stands by it.
  e.phase = 2;
  if (e.lastHit === s.tick) w.blows++;
  if (!w.took && t < S.grab && w.blows >= S.scare) {
    ev(s, { t: 'senda', kind: 'scared', npc: '', x: e.x, y: e.y, n: w.pan });
    dive(s, w, e);
    return;
  }
  if (!w.took && t >= S.grab) {
    w.took = true;
    let pick = -1;
    w.on.forEach((p, i) => {
      if (p === w.pan && (pick < 0 || (r.pieces[i] as { w: number }).w > (r.pieces[pick] as { w: number }).w)) pick = i;
    });
    if (pick >= 0) {
      const stone = (r.pieces[pick] as { stone: boolean }).stone;
      w.on[pick] = stone ? -4 : -3;
      w.back[pick] = stone ? s.tick + r.restock : w.pan;
      const p = r.pans[w.pan] as { x: number; y: number };
      ev(s, { t: 'senda', kind: 'throw', npc: '', x: p.x, y: p.y, n: pick });
      cue('throw');
    }
  }
  const hero = s.heroes.find((h) => h.downTicks === 0);
  if (hero && e.cd === 0 && withinRange(e.x, e.y, hero.x, hero.y, 1300)) {
    e.facing = 0;
    attackHero(s, e, hero);
  }
  if (e.cd > 0) e.cd--;
  if (t >= S.grab + S.up) dive(s, w, e);
};

const dive = (s: BattleState, w: WeightState, e: Enemy): void => {
  w.phase = 0;
  w.since = s.tick;
  e.phase = 1;
  ev(s, { t: 'senda', kind: 'sink', npc: '', x: e.x, y: e.y, n: 0 });
};

/** Kel hauls a hanging shield back up (standing by its pan, if nothing strikes him), then takes it to the pile. */
const kelTick = (s: BattleState, r: WeightRules, w: WeightState): void => {
  const kel = kelOf(s);
  if (!kel || kel.hp <= 0) return;
  kel.moving = false;
  if (w.haul < 0) {
    const p = w.on.findIndex((x) => x === -3);
    if (p >= 0) {
      w.haul = p;
      w.hauled = 0;
    }
  }
  const home = { x: r.pile.x - 900, y: r.pile.y + 200 };
  if (w.haul < 0) {
    if (!withinRange(kel.x, kel.y, home.x, home.y, 100)) {
      kel.moving = true;
      stepToward(kel, home.x, home.y, 70);
    }
    if (kel.flinch > 0) kel.flinch--;
    return;
  }
  const pan = r.pans[w.back[w.haul] as number] ?? r.pans[1];
  const at = { x: (pan as { x: number }).x - 600, y: (pan as { y: number }).y + 300 };
  if (!withinRange(kel.x, kel.y, at.x, at.y, 120)) {
    kel.moving = true;
    stepToward(kel, at.x, at.y, 70);
    return;
  }
  if (kel.flinch > 0) {
    kel.flinch--;
    return;
  }
  if (++w.hauled >= r.haul) {
    w.on[w.haul] = -1;
    w.back[w.haul] = 0;
    ev(s, { t: 'senda', kind: 'haul', npc: 'kel', x: kel.x, y: kel.y, n: w.haul });
    w.haul = -1;
    w.hauled = 0;
  }
};

export const weightTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.weight;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.weight ??= fresh(r));
  if (!s.started) return;
  heroWork(s, r, w);
  // Stones down the drop come back to the pile in their time.
  w.on.forEach((p, i) => {
    if (p === -4 && s.tick >= (w.back[i] as number)) {
      w.on[i] = -1;
      w.back[i] = 0;
    }
  });
  kelTick(s, r, w);
  const level = weightLevel(r, w);
  if (level && w.level < 0) {
    w.level = s.tick;
    ev(s, { t: 'senda', kind: 'level', npc: '', x: (r.pans[1] as { x: number }).x, y: (r.pans[1] as { y: number }).y });
    cue('level');
  } else if (!level) {
    w.level = -1;
    // Every shield hung and still not level: the spans tip (Kel says so, once).
    if (shieldsUp(r, w) && w.job < 0) cue('wrong');
  }
  // The bolts in: a serpent still alive sinks for good.
  if (w.level >= 0 && s.tick - w.level >= r.hold) {
    const e = w.snake ? findEnemy(s, w.snake) : null;
    if (e && e.hp > 0) {
      ev(s, { t: 'senda', kind: 'sink', npc: '', x: e.x, y: e.y, n: 1 });
      s.enemies = s.enemies.filter((x) => x.id !== e.id);
    }
    return;
  }
  serpentTick(s, r, w, cue);
};

/** The serpent's own turn in the nightmares' loop: it moves only by its rule (true: nothing else for it). */
export const weightSerpent = (s: BattleState, e: Enemy): boolean => !!s.rules?.senda?.weight && e.id === s.senda?.weight?.snake;

/** How many spans hang level. */
export const weightGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.weight;
  const w = s.senda?.weight;
  if (!r || !w) return { done: 0, need: 3 };
  const p = panPulls(r, w);
  return { done: p.filter((x, i) => x === r.posts[i]).length, need: 3 };
};

/** Won with the spans level and the bolts in. */
export const weightResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.weight;
  const w = s.senda?.weight;
  return r && w && w.level >= 0 && s.tick - w.level >= r.hold ? 'won' : null;
};

/** The hero carrying a piece (its pace: SENDA.weight.carryBp). */
export const weightCarrying = (s: BattleState): boolean => !!s.senda?.weight?.on.includes(-2);
